import { ChartData, ChartSeries } from '../types';

export const FAN_NEVER_OFF_MESSAGE = 'Fan never turned off, no measurable drop';

export interface DatedDelta {
  date: string;
  value: number | null;
}

export type DeltaSide = 'return' | 'fan';
export type DeltaRow = Record<string, string | number | null> & { date: string };

export const measuredDeltas = (data: ChartData, side: DeltaSide): DatedDelta[] => {
  const deltas = side === 'return' ? data.return_deltas : data.fan_deltas;
  if (!deltas) return data.fan_never_off?.includes(true)
    ? data.dates.map((date) => ({ date, value: null })) : [];
  if (!Array.isArray(deltas)) {
    const points: DatedDelta[] = (deltas.transitions ?? [])
      .map(({ timestamp, delta }) => ({ date: timestamp, value: delta }));
    data.dates.forEach((date) => {
      if (!points.some((point) => point.date.slice(0, 10) === date)) {
        points.push({ date, value: null });
      }
    });
    return points.sort((a, b) => a.date.localeCompare(b.date));
  }
  return data.dates.map((date, index) => ({ date, value: deltas[index] ?? null }));
};

export const hasMeasurements = (values: DatedDelta[]): boolean =>
  values.some(({ value }) => value !== null);

export const buildDeltaRows = (series: ChartSeries[], side: DeltaSide): DeltaRow[] => {
  const values = series.map(({ data }) => measuredDeltas(data, side));
  const dates = new Set<string>();
  values.forEach((points, seriesIndex) => {
    const data = series[seriesIndex].data;
    points.forEach(({ date, value }) => {
      if (value !== null || data.fan_never_off?.[data.dates.indexOf(date)] === true) dates.add(date);
    });
  });
  const sortedDates = Array.from(dates).sort();
  return sortedDates.map((date) => {
    const row: DeltaRow = { date };
    series.forEach(({ id, data }, seriesIndex) => {
      const points = values[seriesIndex];
      const index = points.findIndex((point) => point.date === date);
      const measured = index >= 0 ? points[index].value : null;
      row[`delta_${id}`] = measured;
      let placeholder: number | null = null;
      if (index >= 0 && measured === null && data.fan_never_off?.[data.dates.indexOf(date)] === true) {
        const before = points.slice(0, index).reverse().find((point) => point.value !== null);
        const after = points.slice(index + 1).find((point) => point.value !== null);
        placeholder = before?.value ?? after?.value ?? null;
      }
      if (data.fan_never_off?.includes(true)) row[`hollow_${id}`] = placeholder;
    });
    return row;
  });
};
